import React, { useEffect, useState } from "react";
import {
  Card,
  Form,
  Input,
  Button,
  Switch,
  message,
  Divider,
  Table,
  Space,
  Select,
  Modal,
  Popconfirm,
  Spin,
} from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import fetch from "auth/FetchInterceptor";
import LeadFormService from "services/LeadFormService";

const FIELD_TYPES = [
  { label: "Texto", value: "TEXT" },
  { label: "E-mail", value: "EMAIL" },
  { label: "Telefone", value: "PHONE" },
  { label: "CPF", value: "CPF" },
  { label: "Número", value: "NUMBER" },
  { label: "Data", value: "DATE" },
  { label: "Lista Select", value: "SELECT" },
  { label: "Radio", value: "RADIO" },
  { label: "Checkbox", value: "CHECKBOX" },
  { label: "Texto longo", value: "TEXTAREA" },
];

const normalizeArray = (res) => {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.items)) return res.items;
  return [];
};

const LeadFormEdit = () => {
  const { id } = useParams();
  const isNew = id === "new";
  const navigate = useNavigate();

  const [form] = Form.useForm();
  const [fieldForm] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [data, setData] = useState(null);

  const [events, setEvents] = useState([]);
  const [games, setGames] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [savingField, setSavingField] = useState(false);

  const loadOptions = async () => {
    setLoadingOptions(true);

    try {
      const eventsRes = await fetch({
        url: "/events",
        method: "get",
      });

      const gamesRes = await fetch({
        url: "/games",
        method: "get",
      });

      setEvents(normalizeArray(eventsRes));
      setGames(normalizeArray(gamesRes));
    } catch (err) {
      message.error("Erro ao carregar eventos e jogos");
    } finally {
      setLoadingOptions(false);
    }
  };

  const load = async () => {
    if (isNew) return;

    setLoading(true);

    try {
      const res = await LeadFormService.get(Number(id));
      setData(res);

      form.setFieldsValue({
        name: res.name,
        description: res.description,
        eventId: res.eventId || undefined,
        gameId: res.gameId || undefined,
        isEnabled: res.isEnabled,
        showBeforeGame: res.showBeforeGame,
        requireConsent: res.requireConsent,
        consentText: res.consentText,
      });
    } catch (err) {
      message.error("Erro ao carregar formulário");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOptions();
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const save = async () => {
    try {
      const values = await form.validateFields();

      const payload = {
        name: values.name,
        description: values.description || null,
        eventId: values.eventId || null,
        gameId: values.gameId || null,
        isEnabled: values.isEnabled ?? true,
        showBeforeGame: values.showBeforeGame ?? true,
        requireConsent: values.requireConsent ?? true,
        consentText: values.consentText || null,
      };

      console.log("PAYLOAD FORM:", payload);

      if (isNew) {
        const created = await LeadFormService.create(payload);
        message.success("Formulário criado");
        navigate(`/app/lead-forms/${created.id}`);
      } else {
        await LeadFormService.update(Number(id), payload);
        message.success("Formulário atualizado");
        load();
      }
    } catch (err) {
      console.error(err);
      message.error("Verifique os campos obrigatórios");
    }
  };

  const addField = async () => {
    try {
      const values = await fieldForm.validateFields();

      let options = null;

      if (values.optionsText) {
        options = values.optionsText
          .split("\n")
          .map((item) => item.trim())
          .filter(Boolean);
      }

      setSavingField(true);

      await LeadFormService.addField(Number(id), {
        label: values.label,
        fieldKey: values.fieldKey || undefined,
        fieldType: values.fieldType,
        placeholder: values.placeholder || undefined,
        isRequired: values.isRequired || false,
        order: values.order ? Number(values.order) : 99,
        options,
      });

      message.success("Campo criado");
      fieldForm.resetFields();
      setModalOpen(false);
      load();
    } catch (err) {
      message.error(err?.response?.data?.message || "Erro ao criar campo");
    } finally {
      setSavingField(false);
    }
  };

  const removeField = async (fieldId) => {
    try {
      await LeadFormService.removeField(fieldId);
      message.success("Campo removido");
      load();
    } catch (err) {
      message.error("Erro ao remover campo");
    }
  };

  const eventOptions = events.map((event) => ({
    label: event.name || event.title || `Evento ${event.id}`,
    value: event.id,
  }));

  const gameOptions = games.map((game) => ({
    label: game.event?.name
      ? `${game.name || game.title || `Game ${game.id}`} - ${game.event.name}`
      : game.eventName
        ? `${game.name || game.title || `Game ${game.id}`} - ${game.eventName}`
        : game.name || game.title || `Game ${game.id}`,
    value: game.id,
  }));

  const columns = [
    {
      title: "Ordem",
      dataIndex: "order",
      key: "order",
      width: 80,
    },
    {
      title: "Campo",
      dataIndex: "label",
      key: "label",
    },
    {
      title: "Chave",
      dataIndex: "fieldKey",
      key: "fieldKey",
    },
    {
      title: "Tipo",
      dataIndex: "fieldType",
      key: "fieldType",
    },
    {
      title: "Obrigatório",
      dataIndex: "isRequired",
      key: "isRequired",
      render: (value) => (value ? "Sim" : "Não"),
    },
    {
      title: "Ações",
      key: "actions",
      render: (_, row) => (
        <Popconfirm
          title="Remover campo?"
          okText="Sim"
          cancelText="Não"
          onConfirm={() => removeField(row.id)}
        >
          <Button danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  return (
    <Card
      title={isNew ? "Novo formulário" : "Editar formulário"}
      loading={loading}
      extra={
        <Space>
          <Button onClick={() => navigate("/app/lead-forms")}>Voltar</Button>

          <Button type="primary" onClick={save}>
            Salvar
          </Button>
        </Space>
      }
    >
      <Spin spinning={loadingOptions}>
        <Form
          layout="vertical"
          form={form}
          initialValues={{
            isEnabled: true,
            showBeforeGame: true,
            requireConsent: true,
            consentText:
              "Autorizo o uso dos meus dados para contato e ações promocionais.",
          }}
        >
          <Form.Item
            label="Nome do formulário"
            name="name"
            rules={[
              { required: true, message: "Informe o nome do formulário" },
            ]}
          >
            <Input placeholder="Ex: Formulário Roleta BGS" />
          </Form.Item>

          <Form.Item label="Descrição" name="description">
            <Input.TextArea
              rows={3}
              placeholder="Ex: Preencha seus dados para participar"
            />
          </Form.Item>

          <Form.Item
            label="Evento"
            name="eventId"
            rules={[{ required: true, message: "Selecione o evento" }]}
          >
            <Select
              showSearch
              placeholder="Selecione o evento"
              optionFilterProp="label"
              options={eventOptions}
            />
          </Form.Item>

          <Form.Item
            label="Game vinculado"
            name="gameId"
            rules={[{ required: true, message: "Selecione o game" }]}
          >
            <Select
              showSearch
              placeholder="Selecione o game"
              optionFilterProp="label"
              options={gameOptions}
            />
          </Form.Item>

          <Space size="large" wrap>
            <Form.Item label="Ativo" name="isEnabled" valuePropName="checked">
              <Switch />
            </Form.Item>

            <Form.Item
              label="Exibir antes do jogo"
              name="showBeforeGame"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>

            <Form.Item
              label="Exigir LGPD"
              name="requireConsent"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </Space>

          <Form.Item label="Texto LGPD" name="consentText">
            <Input.TextArea
              rows={3}
              placeholder="Autorizo o uso dos meus dados para contato..."
            />
          </Form.Item>
        </Form>
      </Spin>

      {!isNew && (
        <>
          <Divider />

          <Card
            type="inner"
            title="Campos do formulário"
            extra={
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setModalOpen(true)}
              >
                Adicionar campo
              </Button>
            }
          >
            <Table
              rowKey="id"
              columns={columns}
              dataSource={data?.fields || []}
              pagination={false}
            />
          </Card>
        </>
      )}

      <Modal
        title="Adicionar campo"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={addField}
        confirmLoading={savingField}
        okText="Salvar"
        cancelText="Cancelar"
        destroyOnClose
      >
        <Form
          layout="vertical"
          form={fieldForm}
          initialValues={{
            fieldType: "TEXT",
            order: 99,
            isRequired: false,
          }}
        >
          <Form.Item
            label="Nome do campo"
            name="label"
            rules={[{ required: true, message: "Informe o nome do campo" }]}
          >
            <Input placeholder="Ex: Empresa" />
          </Form.Item>

          <Form.Item label="Chave do campo" name="fieldKey">
            <Input placeholder="Ex: empresa" />
          </Form.Item>

          <Form.Item
            label="Tipo"
            name="fieldType"
            rules={[{ required: true, message: "Selecione o tipo" }]}
          >
            <Select options={FIELD_TYPES} />
          </Form.Item>

          <Form.Item label="Placeholder" name="placeholder">
            <Input placeholder="Ex: Digite sua empresa" />
          </Form.Item>

          <Form.Item label="Ordem" name="order">
            <Input type="number" placeholder="Ex: 4" />
          </Form.Item>

          <Form.Item
            label="Obrigatório"
            name="isRequired"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item label="Opções" name="optionsText">
            <Input.TextArea
              rows={4}
              placeholder={
                "Use apenas para SELECT, RADIO ou CHECKBOX\nDigite uma opção por linha"
              }
            />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default LeadFormEdit;